// One-time cinematic "WINDOW UNSEALED" overlay, played the first time a visitor
// sees an application form after its window opens.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STAMP = {
  student: "APPLICATIONS SEALED",
  talent: "APPLICATIONS SEALED",
  brand: "NETWORK ACCESS RESTRICTED",
  mentor: "NETWORK ACCESS RESTRICTED",
};

const SUB = {
  student: "Recruitment window open · Submit your dossier",
  talent: "Recruitment window open · Submit your file",
  brand: "Network window open · Transmit your brief",
  mentor: "Network window open · Report for duty",
};

const storageKey = (kind) => `blv_unsealed_${kind === "talent" ? "student" : kind === "mentor" ? "brand" : kind}`;

export const UnsealReveal = ({ kind = "student" }) => {
  const [stage, setStage] = useState(null); // seal | break | reveal | null

  useEffect(() => {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      if (localStorage.getItem(storageKey(kind))) return;
      const isQA = new URLSearchParams(window.location.search).has("asof");
      if (!isQA) localStorage.setItem(storageKey(kind), "1");
    } catch {}
    setStage("seal");
    const t1 = setTimeout(() => setStage("break"), 1400);
    const t2 = setTimeout(() => setStage("reveal"), 2200);
    const t3 = setTimeout(() => setStage(null), 5200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [kind]);

  return (
    <AnimatePresence>
      {stage && (
        <motion.div
          key="unseal"
          className="fixed inset-0 z-[90] flex items-center justify-center overflow-hidden bg-onyx"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.7 } }}
          data-testid={`unseal-reveal-${kind}`}
        >
          <div className="grain-dark pointer-events-none absolute inset-0" />
          <div className="vignette pointer-events-none absolute inset-0" />
          <div className="drift-slow pointer-events-none absolute left-1/2 top-1/2 h-[80vmin] w-[80vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(201,162,39,0.12) 0%, transparent 60%)" }} />

          {/* The seal — shakes, then splits apart */}
          {(stage === "seal" || stage === "break") && (
            <div className="relative text-center">
              <motion.p
                className="font-mono text-[9px] uppercase tracking-[0.4em] text-silver/50"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              >
                Case file BLV-2027 · Status update incoming
              </motion.p>
              <div className="relative mt-6">
                {["left", "right"].map((half) => (
                  <motion.div
                    key={half}
                    className={half === "right" ? "absolute inset-0" : "relative"}
                    style={{ clipPath: half === "left" ? "polygon(0 0, 58% 0, 42% 100%, 0 100%)" : "polygon(58% 0, 100% 0, 100% 100%, 42% 100%)" }}
                    initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
                    animate={
                      stage === "break"
                        ? { x: half === "left" ? -140 : 140, y: half === "left" ? 30 : -40, rotate: half === "left" ? -14 : 12, opacity: 0 }
                        : { x: [0, -2, 3, -3, 2, 0], transition: { delay: 0.8, duration: 0.5 } }
                    }
                    transition={stage === "break" ? { duration: 0.7, ease: [0.3, 0, 0.7, 0.2] } : undefined}
                  >
                    <div className="inline-block -rotate-3 border-2 border-gold bg-onyx px-6 py-3 shadow-[0_0_40px_rgba(201,162,39,0.25)]">
                      <span className="border border-gold/40 px-4 py-1.5 font-display text-3xl uppercase tracking-[0.15em] text-gold sm:text-4xl">
                        {STAMP[kind] || STAMP.student}
                      </span>
                    </div>
                  </motion.div>
                ))}
                {stage === "break" && (
                  <motion.div
                    className="pointer-events-none absolute left-1/2 top-1/2 h-[140%] w-[3px] -translate-x-1/2 -translate-y-1/2 rotate-[18deg] bg-gradient-to-b from-transparent via-goldlight to-transparent"
                    initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: [0, 1, 0] }} transition={{ duration: 0.5 }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Gold flash on break */}
          {stage === "break" && (
            <motion.div className="pointer-events-none absolute inset-0 bg-gold"
              initial={{ opacity: 0 }} animate={{ opacity: [0, 0.22, 0] }} transition={{ duration: 0.6 }} />
          )}

          {/* WINDOW UNSEALED */}
          {stage === "reveal" && (
            <div className="relative px-6 text-center">
              <motion.p
                className="font-mono text-[10px] uppercase tracking-[0.5em] text-gold"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              >
                ● Access granted
              </motion.p>
              <motion.h2
                className="relative mt-4 overflow-hidden font-display text-5xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-7xl"
                initial={{ opacity: 0, scale: 0.94, letterSpacing: "0.1em" }}
                animate={{ opacity: 1, scale: 1, letterSpacing: "0em" }}
                transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                data-testid="unseal-title"
              >
                Window Unsealed
                <motion.span
                  className="pointer-events-none absolute inset-y-0 w-1/3"
                  style={{ background: "linear-gradient(105deg, transparent, rgba(244,239,230,0.35), transparent)" }}
                  initial={{ left: "-40%" }} animate={{ left: "120%" }} transition={{ delay: 0.5, duration: 0.9, ease: "easeInOut" }}
                />
              </motion.h2>
              <motion.div className="gold-rule mx-auto mt-6 w-56"
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.7, duration: 0.6 }} />
              <motion.p
                className="mt-5 font-cinzel text-[11px] uppercase tracking-[0.35em] text-cream/90 sm:text-sm"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              >
                {SUB[kind] || SUB.student}
              </motion.p>
              <motion.p className="mt-8 font-marker text-sm text-silver/60"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
                the Architect is watching. make it count.
              </motion.p>
            </div>
          )}

          <button
            onClick={() => setStage(null)}
            className="absolute bottom-6 right-6 border border-silver/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/70 transition-colors hover:border-gold hover:text-goldlight"
            data-testid="unseal-skip-btn"
          >
            Skip
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
